import { SUCCESS_100 } from '@mnd-frontend/ui/shared/tokens';
import type { ChangeEvent, FocusEvent } from 'react';
import { useId } from 'react';
import styled, { css } from 'styled-components';
import { SrOnly } from '../SrOnly';

const Wrapper = styled.div`
  position: relative;
`;

const StyledSelect = styled.select<{ $hasError?: boolean; $valid?: boolean }>`
  -webkit-appearance: none;
  appearance: none;

  height: 56px;
  background-color: var(--form-field-bg);
  border: 1px solid var(--form-field-border-color);
  border-radius: 0;
  color: var(--text-color);
  display: inline-block;
  font-family: var(--body-font-family);
  font-size: 0.9375rem;

  line-height: 1.5;
  margin: 0;
  max-width: 100%;
  outline: 0;
  padding: var(--form-field-padding-y) var(--form-field-padding-x);
  text-align: left;
  text-decoration: none;
  text-transform: none;
  width: 100%;

  transition: all 0.25s 0s var(--easing);

  &:focus {
    border-color: var(--form-field-focus-border-color);
    background-color: var(--form-field-focus-bg);
  }

  option {
    padding: 0;
  }

  appearance: none;
  background-image: url("https://www.svgrepo.com/show/80156/down-arrow.svg");
  background-repeat: no-repeat;
  background-size: 10px 10px;
  background-position: calc(100% - 1.25rem);

  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: var(--color-red);
      background-color: var(--form-field-focus-bg);
      &:focus {
        border-color: var(--color-red);
      }
    `}

  ${({ $valid }) =>
    $valid &&
    css`
      background-color: ${SUCCESS_100};
    `}
`;

type Props = {
  name: string;
  label: string;
  options: string[][];
  defaultValue?: string;
  required?: boolean;
  errors?: string[];
  onBlur?: (e: FocusEvent<HTMLSelectElement>) => void;
  valid?: boolean;
  value?: string | number | string[];
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
};

const ErrorText = styled.span`
  color: var(--color-black);
  font-size: var(--font-size-x-small);
  text-align: right;
  white-space: pre-line;
`;

const Select = ({
  name,
  label,
  options,
  defaultValue,
  errors,
  onBlur,
  value,
  onChange,
  valid,
  required,
}: Props) => {
  const hasErrors = Boolean(errors && errors.length > 0);
  const internalId = useId();

  return (
    <>
      <Wrapper>
        <SrOnly as="label" htmlFor="select-country">
          {label}
        </SrOnly>
        <StyledSelect
          name={name}
          defaultValue={defaultValue}
          $hasError={hasErrors}
          $valid={valid}
          required={required}
          id="select-country"
          onBlur={onBlur}
          value={value}
          onChange={onChange}
          aria-describedby={hasErrors ? internalId : undefined}
        >
          <option value="" disabled hidden>
            {label}
          </option>
          {options.map(([key, value]) => (
            <option key={key} value={key} disabled={value === '---'}>
              {value}
            </option>
          ))}
        </StyledSelect>
      </Wrapper>
      {hasErrors && <ErrorText id={internalId}>{errors?.join('\n\n')}</ErrorText>}
    </>
  );
};

export default Select;
